// NL-datum input wrapper rondom de bestaande Input-component.
// `value` is altijd ISO YYYY-MM-DD (= wat de backend verwacht); de UI toont
// dd-mm-jjjj. Eén plek voor formaat-conversie zodat formulieren ISO blijven
// gebruiken zonder per-veld parsers te hoeven kennen.

import { useEffect, useState } from 'react';
import { Input } from './input';

export function isoToNl(iso: string | null | undefined): string {
	if (!iso) return '';
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!m) return '';
	return `${m[3]}-${m[2]}-${m[1]}`;
}

export function nlToIso(nl: string): string | null {
	const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(nl);
	if (!m) return null;
	const day = m[1];
	const month = m[2];
	const year = m[3];
	const iso = `${year}-${month}-${day}`;
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	// Round-trip-check vangt 31-02-yyyy etc. af.
	if (d.toISOString().slice(0, 10) !== iso) return null;
	return iso;
}

interface NlDateInputProps {
	value: string; // ISO YYYY-MM-DD of ''
	onChange: (iso: string) => void; // ISO of '' bij ongeldige/lege input
	disabled?: boolean;
	placeholder?: string;
	className?: string;
	id?: string;
}

export function NlDateInput({
	value,
	onChange,
	disabled,
	placeholder = 'dd-mm-jjjj',
	className,
	id,
}: NlDateInputProps) {
	const [text, setText] = useState(isoToNl(value));

	// Sync vanuit parent (form-reset, programmatische value-set).
	useEffect(() => {
		const next = isoToNl(value);
		setText((prev) => (prev === next ? prev : next));
	}, [value]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;
		// Strip alles behalve cijfers en koppelteken; auto-insert koppelteken na
		// dag (pos 2) en maand (pos 5) zodat typen "01012025" → "01-01-2025".
		const digits = raw.replace(/[^\d]/g, '').slice(0, 8);
		let formatted = digits;
		if (digits.length > 4) {
			formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
		} else if (digits.length > 2) {
			formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
		}
		setText(formatted);
		if (formatted === '') {
			onChange('');
			return;
		}
		const iso = nlToIso(formatted);
		if (iso) onChange(iso);
		// Bij ongeldig (incomplete invoer) onChange niet triggeren; parent houdt
		// laatste valide ISO. Parent kan eigen validatie tonen op submit.
	};

	const handleBlur = () => {
		// Bij blur: als tekst niet valideert, terugzetten naar laatst-bekende ISO.
		const iso = nlToIso(text);
		if (!iso && value) setText(isoToNl(value));
	};

	return (
		<Input
			id={id}
			type="text"
			inputMode="numeric"
			value={text}
			onChange={handleChange}
			onBlur={handleBlur}
			placeholder={placeholder}
			className={className}
			disabled={disabled}
			autoComplete="off"
		/>
	);
}
